import React, { useState, RefObject, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { IconCloseFill, IconSearchFill } from '@saucelabs/ds-icons';
import { Loader } from '@saucelabs/sl-component-loader';

import { ValueOf } from '../../../../globalTypes';
import { GlobalState } from '../../../../reducers';
import { CONFIGURATION_TEST_TYPES } from '../../../../constants';
import { selectors as testConfigurationSelectors } from '../../../testConfiguration';
import { trackEvent, MANUAL_EVENTS } from '../../../../tracking';
import Styles from './SearchInput.styles';

const TRACK_SEARCH_DEBOUNCE_THRESHOLD_MS = 800;

export type SearchInputProps = {
  blur: () => void;
  focus: () => void;
  keyboardShortcuts?: boolean;
  loading?: boolean;
  numberOfResults?: number | null;
  onSearch: (searchTerm: string) => void;
  searchInputRef: RefObject<HTMLInputElement>;
  searchOnTextChange?: boolean;
}

const SearchInput: React.FC<SearchInputProps> = ({
  blur,
  focus,
  keyboardShortcuts = false,
  loading = false,
  numberOfResults = 0,
  onSearch,
  searchInputRef,
  searchOnTextChange = false,
}) => {
  const [searchText, setSearchText] = useState('');
  const testType = useSelector<GlobalState, ValueOf<typeof CONFIGURATION_TEST_TYPES>>(
    testConfigurationSelectors.testTypeSelector,
  );

  const trackSearchDebounced = useMemo(() => (
    debounce((searchTerm: string) => {
      if (searchTerm) {
        trackEvent(MANUAL_EVENTS.SEARCH_REAL_DEVICE, {
          input: searchTerm,
          testType,
        });
      }
    }, TRACK_SEARCH_DEBOUNCE_THRESHOLD_MS)
  ), [testType]);

  const executeSearch = useCallback((searchTerm: string) => {
    onSearch(searchTerm);
    trackSearchDebounced(searchTerm);
  }, [onSearch, trackSearchDebounced]);

  // cancel pending tracking if user navigates away
  useEffect(() => () => trackSearchDebounced.cancel(), [trackSearchDebounced]);

  return (
    <div className={Styles.default}>
      <input
        ref={searchInputRef}
        className={`${Styles.input} text-high-emphasis focus:outline-none focus:bg-gray-50`}
        onBlur={blur}
        onChange={(event) => {
          setSearchText(event.target.value);
          if (searchOnTextChange) { executeSearch(event.target.value); }
        }}
        onFocus={focus}
        onKeyDown={(event) => {
          if (event.key === 'Enter') { executeSearch(searchText); }
        }}
        role="searchbox"
        type="text"
        value={searchText}
      />
      <button
        aria-label="button-search"
        className={Styles['button-search']}
        onClick={() => executeSearch(searchText)}
        onMouseDown={(event) => event.preventDefault()}
        tabIndex={-1}
        type="button"
      >
        <IconSearchFill />
      </button>
      {!searchText && (
        <div className={`${Styles.placeholder} text-gray-disabled`}>
          { keyboardShortcuts ? (
            <>
              Type to
              {' '}
              <u>S</u>
              earch&#8230;
            </>
          ) : 'Type to Search...'}
        </div>
      )}

      {/* Show loader or the number of results! */}
      {loading ? (
        <div
          className={Styles.loader}
          data-qa="manual-search-filter-search-input-loading"
        >
          <Loader fontSize="20px" />
        </div>
      ) : (
        (typeof numberOfResults === 'number') && (
          <div className={`${Styles.results} text-gray-600`}>{`(${numberOfResults})`}</div>
        )
      )}

      {/* delete icon if text has been entered */}
      {searchText && (
        <button
          aria-label="button-reset"
          className={Styles['button-reset']}
          onClick={() => {
            setSearchText('');
            onSearch('');
          }}
          onMouseDown={(event) => event.preventDefault()}
          tabIndex={-1}
          type="button"
        >
          <IconCloseFill />
        </button>
      )}
    </div>
  );
};

export default SearchInput;
